import React, { useRef } from 'react';
import { IconBrain, IconCheck, IconPlayerPlayFilled } from '@tabler/icons-react';
import { Anchor, Glow, Wires, Sparks, gsap, travel, flare, blink, ambience, entrance, useSceneAnimation } from '../../../../../../../../components/Scene';
import './styles.scss';

// Funnel: the niche's most viral reels pour into the list of what the Studio learned,
// and each one leaves its pattern there (a hook, a myth busted, a call to action),
// which lights up and gets checked. Drawn in the 75–325 band of the stage.
const REELS = [
  { id: 'reel-a', x: 70, views: 2400000, pattern: 'artPatternHook', tone: 'cool' },
  { id: 'reel-b', x: 180, views: 1100000, pattern: 'artPatternMyth', tone: 'violet' },
  { id: 'reel-c', x: 290, views: 870000, pattern: 'artPatternCta', tone: 'warm' },
];
const WIRES = [
  { id: 'reel-a', tone: 'warm', d: 'M70 148 C70 176 150 170 150 200', joints: [[70, 148], [150, 200]] },
  { id: 'reel-b', tone: 'warm', d: 'M180 148 V200', joints: [[180, 148], [180, 200]] },
  { id: 'reel-c', tone: 'warm', d: 'M290 148 C290 176 210 170 210 200', joints: [[290, 148], [210, 200]] },
];
const SPARKS = [[126, 168], [234, 164], [160, 184], [204, 180], [180, 160]];
const STEP = 1.7;

function build(q, { still }) {
  if (still) return;
  ambience(q);
  const intro = entrance(q);
  const rows = q('[data-row]');
  const checks = q('[data-check]');
  const reset = () => {
    gsap.set(rows, { opacity: 0.4 });
    gsap.set(checks, { opacity: 0, scale: 0.5 });
  };
  reset();
  const loop = gsap.timeline({ repeat: -1, repeatDelay: 0.4, delay: intro.duration() - 0.2, onRepeat: reset });
  REELS.forEach(({ id }, i) => {
    const at = i * STEP;
    flare(loop, q, id, at);
    blink(loop, q, [`${id}-0`], at + 0.1);
    travel(loop, q, id, at + 0.12, 0.65);
    blink(loop, q, [`${id}-1`], at + 0.75);
    loop.to(q(`[data-row="${i}"]`), { opacity: 1, duration: 0.3 }, at + 0.8)
      .fromTo(q(`[data-row-glow="${i}"]`), { xPercent: -100, opacity: 1 }, { xPercent: 100, opacity: 0, duration: 0.9, ease: 'power2.out' }, at + 0.8)
      .to(q(`[data-check="${i}"]`), { opacity: 1, scale: 1, duration: 0.35, ease: 'back.out(2.2)' }, at + 0.9);
  });
  loop.to([rows, checks], { opacity: (n, el) => (el.hasAttribute('data-row') ? 0.4 : 0), duration: 0.5 }, '+=1.2');
}

export default function TrainedScene({ t, locale }) {
  const stage = useRef(null);
  useSceneAnimation(stage, build);
  const views = new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 });
  return <div className="studio-feature-art__stage" ref={stage}>
    <Glow x={180} y={110} tone="warm" size={210} />
    <Glow x={180} y={262} tone="cool" size={210} />
    <Sparks points={SPARKS} />
    <Wires wires={WIRES} />
    {REELS.map(({ id, x, views: count }) => <Anchor key={id} x={x} y={112}>
      <span className="studio-landing-art__reel is-warm" data-enter>
        <span className="studio-landing-art__reel-play"><IconPlayerPlayFilled aria-hidden="true" /></span>
        <span className="studio-landing-art__reel-views">{views.format(count)}</span>
        <span className="studio-feature-art__ring" data-ring={id} />
      </span>
    </Anchor>)}
    <Anchor x={180} y={258}>
      <div className="studio-feature-art__card studio-landing-art__list" data-enter>
        <div className="studio-landing-art__list-head"><IconBrain aria-hidden="true" /><span>{t('artViralPatterns')}</span></div>
        {REELS.map(({ id, pattern, tone }, i) => <div key={id} className="studio-landing-art__row" data-row={i}>
          <span className="studio-landing-art__row-glow" data-row-glow={i} />
          <span className={`studio-landing-art__dot is-${tone}`} />
          <span className="studio-landing-art__row-label">{t(pattern)}</span>
          <span className={`studio-landing-art__check is-${tone}`} data-check={i}><IconCheck aria-hidden="true" /></span>
        </div>)}
      </div>
    </Anchor>
  </div>;
}
